import React, { FC } from 'react';

import { graphql } from 'gatsby';
import {
  Accordion,
  Box,
  Heading,
  Icon,
  Text
} from '@lucid/components-react';
import ReactHtmlParser from 'react-html-parser';
import {
  Drupal_ParagraphFlexiFormattedSection,
  Drupal_ParagraphFlexiFormattedSectionList,
  Drupal_ParagraphSeoImage
} from 'generated/drupal-graphql';
import { getProcessedValue } from '@lucid/utils';
import { LucidTheme, mqMin } from '@lucid/theme';

import FlourishedHeading from 'components/flourished-heading/flourished-heading';

interface FlexFormattedSectionListProps {
  entity: Drupal_ParagraphFlexiFormattedSectionList;
}

const FlexiFormattedSectionList: FC<FlexFormattedSectionListProps> = ({
  entity: {
    fieldFlexiFormattedHeader,
    fieldFlexiBody,
    fieldFlexiSeoImage,
    fieldFlexiFormattedSectionLi
  }
}) => {
  const seoImage = fieldFlexiSeoImage?.entity as Drupal_ParagraphSeoImage;

  return (
    <Box
      css={{ label: 'flexiFormattedSectionList' }}
      gridColumn="1 / -1"
      mb={{ default: 5, tablet: 10 }}
      px={{ default: 0, tablet: 6 }}
    >
      <FlourishedHeading
        variant="headline2XL"
      >
        {ReactHtmlParser(getProcessedValue(fieldFlexiFormattedHeader))}
      </FlourishedHeading>
      <Text as="div" css={{ maxWidth: '580px' }} variant="paragraphS400">
        {ReactHtmlParser(getProcessedValue(fieldFlexiBody))}
      </Text>
      <Box display="flex" flexWrap="wrap" justifyContent="space-between" mt={9}>
        <Box
          flex={{
            default: '0 1 100%',
            tablet: '0 1 46%'
          }}
          mb={{
            default: 4,
            tablet: 0
          }}
        >
          {seoImage && (
            <img alt={seoImage.fieldAltText!} loading="lazy" src={seoImage.fieldImageLink!} />
          )}
        </Box>
        <Box
          flex={{
            default: '0 1 100%',
            tablet: '0 1 46%'
          }}
          px={{
            default: 6,
            tablet: 0
          }}
        >
          <Accordion preExpanded={['0']}>
            {fieldFlexiFormattedSectionLi?.map((formattedSection, i) => {
              const {
                fieldFlexiFormattedHeader,
                fieldFlexiBody
              } = formattedSection?.entity as Drupal_ParagraphFlexiFormattedSection;
              return (
                <Accordion.Item
                  key={fieldFlexiFormattedHeader?.value || i}
                  css={{
                    overflowAnchor: 'none'
                  }}
                  variant="transparent"
                >
                  <Accordion.Heading variant="inverse">
                    <Accordion.Button
                      css={{
                        border: 0,
                        position: 'relative',
                        padding: 0
                      }}
                      variant="inverse"
                    >
                      <Heading
                        as="h3"
                        css={(theme: LucidTheme) => ({
                          color: theme.colors.blue[60],
                          marginTop: '20px',
                          marginBottom: '20px',
                          '> u': {
                            display: 'inline-block',
                            position: 'relative',
                            textDecoration: 'none',
                            zIndex: 0,
                            '&::before': {
                              content: '""',
                              background: theme.colors.coolGrey[10],
                              position: 'absolute',
                              height: '50%',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              zIndex: -1
                            }
                          }
                        })}
                        variant="headlineM"
                      >
                        <span
                          css={(theme: LucidTheme) => ({
                            color: theme.colors.coolGrey[100],
                            position: 'absolute',
                            top: 'calc(50% - 9px)',
                            left: -theme.space[5],
                            fontSize: '18px'
                          })}
                        >
                          <Icon type="ArrowRight" />
                        </span>
                        {ReactHtmlParser(
                          getProcessedValue(fieldFlexiFormattedHeader)
                        )}
                      </Heading>
                    </Accordion.Button>
                  </Accordion.Heading>
                  <Accordion.Panel
                    css={{
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      [mqMin.default]: {
                        '&[hidden]': { display: 'block' }
                      },
                      [mqMin.tablet]: {
                        '&[hidden]': { display: 'none' }
                      }
                    }}
                    variant="transparent"
                  >
                    <Text as="div" variant="paragraphS400">
                      {ReactHtmlParser(getProcessedValue(fieldFlexiBody))}
                    </Text>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Box>
      </Box>
    </Box>
  );
};

export default FlexiFormattedSectionList;

export const query = graphql`
  fragment flexiFormattedSectionList on Drupal_ParagraphFlexiFormattedSectionList {
    fieldFlexiFormattedHeader {
      processed
    }
    fieldFlexiBody {
      processed
    }
    fieldFlexiSeoImage {
      entity {
        ... on Drupal_ParagraphSeoImage {
          fieldImageLink
          fieldAltText
        }
      }
    }
    fieldFlexiFormattedSectionLi {
      entity {
        ... on Drupal_ParagraphFlexiFormattedSection {
          fieldFlexiFormattedHeader {
            processed
            value
          }
          fieldFlexiBody {
            processed
          }
        }
      }
    }
  }
`;
